<template>
  <div
    v-if="product"
    :class="productTeaserWideClass"
  >
    <div class="product-teaser-wide__img-wrapper">
      <a
        class="product-teaser-wide__link"
        :class="{
          'product-teaser-wide--product-image': productSrc
        }"
        :href="product.url"
        :aria-label="headlineText"
      >
        <div class="product-teaser-wide__img-wrapper">
          <image-element
            img-class="product-teaser-wide__img"
            :src="[image || productSrc]"
            :alt="headlineText"
            :title="headlineText"
          />
        </div>
      </a>
    </div>
    <div
      class="product-teaser-wide__box"
      :class="{
        'product-teaser-wide--in-basket': product.inBasket
      }"
    >
      <div class="product-teaser-wide__box-top">
        <ratings
          v-if="!product.inBasket"
          class="product-teaser-wide__ratings"
          :rating="product.ratings"
          type="disabled"
        />
        <a
          v-else
          class="product-box__status product-box__status--basket product-box__status--basket-up"
          :href="basketUrl"
        >
          {{ $t('ads.ads_top_info.article_in_basket') }}
        </a>

        <a
          class="product-teaser-wide__link"
          :href="product.url"
        >
          <header class="product-teaser-wide__header | headline headline--type_h2">
            {{ headlineText }}
          </header>
        </a>
        <div
          class="product-teaser-wide__subline"
          v-html="subline || product.shortDescription"
        />
      </div>

      <div class="product-teaser-wide__conversion">
        <div
          v-if="product.zeroPriceItem"
          class="product-teaser__zero-price-wrapper"
        >
          <div class="product-teaser__button-container">
            <span class="product-box__status product-box__status--unavailable">
              {{ $t('availability.no-price') }}
            </span>
          </div>
          <a
            class="product-teaser-wide__specification-link"
            v-if="product.showFoodSpecificationLink"
            :href="foodSpecificationLink"
            v-html="$t('detail.product.characteristics')"
          />
        </div>
        <div
          v-else
          class="product-teaser-wide__price-wrapper"
        >
          <div class="product-teaser-wide__price">
            <price-info
              v-if="!isOnRequestWithoutPrice(product.purchaseOnRequestArticle)"
              :value="product.price"
              :show-star="!settings.price.showTax"
              :show-tax="settings.price.showTax"
            />
          </div>
          <div
            class="product-teaser-wide__button-container"
          >
            <availability-info
              v-if="showAvailability"
              class="product-box__availability"
              :availability="product.availability"
            />
            <product-qty-and-cart-btn
              :product-data="product"
              :product-link="product.url"
              :state="state"
              :cart-button-text="$t('productteaser.addToCart')"
              :recurring-buyable="true"
              cart-button-class="product-teaser-wide__cart-btn"
              @quantityChange="counterChangeHandler"
              @addToBasket="addToBasket"
              @onRequestClicked="openOnRequestLightBox()"
            />
            <a
              class="product-teaser-wide__specification-link"
              v-if="product.showFoodSpecificationLink"
              :href="foodSpecificationLink"
              v-html="$t('detail.product.characteristics')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Options, Prop} from 'vue-property-decorator';
  import {productBoxContainerMixin, ProductBoxContainerClass} from '../../utilities/vueMixins/productBoxContainerMixin/productBoxContainerMixin';
  import type ProductBoxData from '../../types/ProductBoxData';
  import AvailabilityInfo from '../availabilityInfo/availabilityInfo.vue';
  import ProductQtyAndCartBtn from '../productQtyAndCartBtn/productQtyAndCartBtn.vue';
  import Ratings from '../ratings/ratings.vue';
  import LazyLoad from '../lazyLoad/lazyLoad.vue';
  import ImageElement from '../imageElement/imageElement.vue';
  import pigeon from '../../utilities/js/pigeon/pigeon';
  import {isOnRequestWithoutPrice} from '../../utilities/js/onRequestType/OnRequestType'
  import {ProductBoxSettings} from '../productBox/productBoxSettings';
  import diContainer from '../../utilities/js/diContainer/diContainer';
  import jsGlobalConfig from '../../globals/jsGlobalConfig';
  import {usePlaceholder} from '../../utilities/vueMixins/placeholderMixin/placeholder';
  @Options({
    name: 'product-teaser-wide',
    components: {LazyLoad, ImageElement, AvailabilityInfo, ProductQtyAndCartBtn, Ratings},
    mixins: [productBoxContainerMixin],
  })
  export default class ProductTeaserWide extends ProductBoxContainerClass {
    @Prop()
    image: string;
    @Prop()
    headline: string;
    @Prop()
    subline: string;
    productTeaserWideClass = 'product-teaser-wide';
    basketUrl = jsGlobalConfig().getBasketUrl();
    minimumRating = -1;
    isOnRequestWithoutPrice = isOnRequestWithoutPrice;
    settings: ProductBoxSettings = diContainer.get('productBoxSettings');

    mounted() {
      usePlaceholder('product-teaser-wide');
      this.productTeaserWideClass += this.isTooHigh ? ' product-teaser-wide--too-high' : '';
    }

    addToBasket(quantity: number) {
      if (this.product.availability.available || this.product.soldoutRelation?.itemId) {
        this.addToBasketHandler(this.product, quantity);
      }
    }

    get product(): ProductBoxData {
      if (this.listItems && this.listItems.length === 1) {
        return this.listItems[0];
      }
      return this.products[0];
    }

    get headlineText() {
      return this.headline || this.product.name
    }

    get productSrc() {
      if (this.product.images && this.product.images.length > 0) {
        return (this.product.images.length > 1) ? this.product.images[1].src : this.product.images[0].src;
      }
      return null;
    }

    get state() {
      return this.getProductState(this.product);
    }

    get foodSpecificationLink() {
      return this.product.url + this.$t('detail.product.characteristics.section');
    }

    get showAvailability() {
      const availability = this.product.availability;
      const isPurchaseOnRequestArticle = this.product.purchaseOnRequestArticle;
      return availability.statement
        && availability.status === 'LATER_AVAILABLE'
        && !isPurchaseOnRequestArticle;
    }

    get isTooHigh() {
      return this.$el ? this.$el.getBoundingClientRect().height > 400 : false;
    }

    counterChangeHandler(quantity: number) {
      this.quantityChangeHandler(this.product, quantity);
    }

    openOnRequestLightBox() {
      pigeon.publish(
        'lightbox:showUrl',
        window.__globalConfig.onRequestLightBoxUrl
      );
    }
  }
</script>
